/**
 * Message Queue Service
 *
 * Manages local message queue with retry logic, expiration, and dead letter queue.
 * Handles message persistence to sessionStorage and retry backoff calculations.
 */

import type { GetMessagesResponseItem } from "@9amhealth/openapi";
import { MESSAGE_METADATA_KEYS } from "src/state/WebSocketBloc/WebSocketBloc";
import {
  MESSAGE_RETRY,
  DEAD_LETTER_QUEUE,
  STORAGE_KEYS
} from "../constants/ChatConstants";
import reportErrorSentry from "src/lib/reportErrorSentry";
import type { ChatBlocDependencies } from "../types/ChatBlocDependencies";
import type { ChatMessage } from "../ChatBloc";

/**
 * MessageQueue manages the local message queue with:
 * - Retry logic with exponential backoff
 * - Message expiration (24 hours)
 * - Dead letter queue for failed messages
 * - sessionStorage persistence
 */
export class MessageQueue {
  /**
   * The queue of messages waiting to be sent
   */
  private messages: GetMessagesResponseItem[] = [];

  /**
   * Prevents concurrent processing calls
   */
  private isProcessing = false;

  /**
   * Queue of pending process requests while processing is locked
   */
  private pendingProcessRequests: Array<() => void> = [];

  constructor(private deps: ChatBlocDependencies) {}

  /**
   * Get all messages in the queue
   */
  getMessages(): GetMessagesResponseItem[] {
    return this.messages;
  }

  /**
   * Set the entire message queue (used for initialization)
   */
  setMessages(messages: GetMessagesResponseItem[]): void {
    this.messages = messages;
  }

  /**
   * Add a message to the queue with deduplication
   */
  addMessage(message: GetMessagesResponseItem): void {
    let newMessages = [...this.messages, message];
    // Remove duplicate messages, unique by id
    newMessages = newMessages.filter(
      (m, i, self) => i === self.findIndex((m2) => m2.id === m.id)
    );

    this.messages = newMessages;
    this.save();
  }

  /**
   * Remove messages that have been successfully sent (have remote counterparts)
   */
  removeSentMessages(remoteMessages: ChatMessage[]): void {
    const filtered = [...this.messages];

    for (const localMsg of this.messages) {
      const localMeta = localMsg.metadata ?? {};
      const localId = String(localMeta[MESSAGE_METADATA_KEYS.LOCAL_ID]);

      const inRemote = remoteMessages.findIndex((m) => {
        const match = m.computed.localRefId === localId;
        return match;
      });
      if (inRemote !== -1) {
        filtered.splice(filtered.indexOf(localMsg), 1);
      }
    }

    this.messages = filtered;
    this.save();
  }

  /**
   * Get retry count for a message
   */
  getRetryCount(message: GetMessagesResponseItem): number {
    return (
      (message.metadata?.[MESSAGE_METADATA_KEYS.RETRY_COUNT] as number) || 0
    );
  }

  /**
   * Check if message has exceeded max retries
   */
  hasExceededRetries(message: GetMessagesResponseItem): boolean {
    return this.getRetryCount(message) >= MESSAGE_RETRY.MAX_RETRIES;
  }

  /**
   * Calculate next retry delay using exponential backoff
   */
  calculateNextRetryDelay(message: GetMessagesResponseItem): number {
    const retryCount = this.getRetryCount(message);
    return Math.min(
      MESSAGE_RETRY.BASE_DELAY_MS * Math.pow(2, retryCount),
      MESSAGE_RETRY.MAX_DELAY_MS
    );
  }

  /**
   * Check if message is ready for retry (backoff has expired)
   */
  isReadyForRetry(message: GetMessagesResponseItem): boolean {
    const lastRetryTime = message.metadata?.[
      MESSAGE_METADATA_KEYS.LAST_RETRY_TIME
    ] as number | undefined;
    if (!lastRetryTime) {
      return true; // Never retried, ready now
    }
    const nextRetryDelay = this.calculateNextRetryDelay(message);
    const timeSinceLastRetry = Date.now() - lastRetryTime;
    return timeSinceLastRetry >= nextRetryDelay;
  }

  /**
   * Increment retry count and update timestamps
   */
  incrementRetry(message: GetMessagesResponseItem): GetMessagesResponseItem {
    const currentCount = this.getRetryCount(message);
    return {
      ...message,
      metadata: {
        ...message.metadata,
        [MESSAGE_METADATA_KEYS.RETRY_COUNT]: currentCount + 1,
        [MESSAGE_METADATA_KEYS.LAST_RETRY_TIME]: Date.now(),
        [MESSAGE_METADATA_KEYS.FIRST_ATTEMPT_TIME]:
          message.metadata?.[MESSAGE_METADATA_KEYS.FIRST_ATTEMPT_TIME] ||
          Date.now()
      }
    };
  }

  /**
   * Update a message in the queue
   */
  updateMessage(
    localId: string,
    updatedMessage: GetMessagesResponseItem
  ): void {
    const messageIndex = this.messages.findIndex(
      (m) => m.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID] === localId
    );

    if (messageIndex !== -1) {
      this.messages[messageIndex] = updatedMessage;
      this.save();
    }
  }

  /**
   * Remove a message from the queue
   */
  removeMessage(localId: string): void {
    this.messages = this.messages.filter(
      (m) => m.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID] !== localId
    );
    this.save();
  }

  /**
   * Check if message has expired (older than 24 hours)
   */
  isExpired(message: GetMessagesResponseItem): boolean {
    const messageAge =
      Date.now() -
      new Date(
        String(message.metadata?.[MESSAGE_METADATA_KEYS.SEND_TIMESTAMP] ?? "")
      ).getTime();

    return messageAge > MESSAGE_RETRY.MAX_MESSAGE_AGE_MS;
  }

  /**
   * Move message to dead letter queue
   */
  moveToDeadLetterQueue(message: GetMessagesResponseItem): void {
    try {
      const deadLetterQueue = this.getDeadLetterMessages();

      // Add failure metadata
      const failedMessage = {
        ...message,
        metadata: {
          ...message.metadata,
          [MESSAGE_METADATA_KEYS.FAILED_AT]: Date.now(),
          [MESSAGE_METADATA_KEYS.FINAL_RETRY_COUNT]: this.getRetryCount(message)
        }
      };

      deadLetterQueue.push(failedMessage);

      // Keep only last N failed messages
      const trimmed = deadLetterQueue.slice(
        DEAD_LETTER_QUEUE.MAX_MESSAGES * -1
      );

      this.deps.storage.setItem(
        DEAD_LETTER_QUEUE.STORAGE_KEY,
        JSON.stringify(trimmed)
      );

      // Report to Sentry for monitoring
      reportErrorSentry(new Error("Message moved to dead letter queue"), {
        messageId: message.id,
        localId: message.metadata?.[MESSAGE_METADATA_KEYS.LOCAL_ID],
        retryCount: this.getRetryCount(message),
        age:
          Date.now() -
          ((message.metadata?.[
            MESSAGE_METADATA_KEYS.FIRST_ATTEMPT_TIME
          ] as number) || Date.now())
      });
    } catch (error) {
      reportErrorSentry(
        new Error("Failed to move message to dead letter queue", {
          cause: error
        })
      );
    }
  }

  /**
   * Get messages from dead letter queue
   */
  getDeadLetterMessages(): GetMessagesResponseItem[] {
    const existing = this.deps.storage.getItem(DEAD_LETTER_QUEUE.STORAGE_KEY);
    const deadLetterQueue: GetMessagesResponseItem[] = existing
      ? (JSON.parse(existing) as GetMessagesResponseItem[])
      : [];
    return deadLetterQueue;
  }

  /**
   * Save queue to sessionStorage
   */
  save(): void {
    this.deps.storage.setItem(
      STORAGE_KEYS.LOCAL_MESSAGES,
      JSON.stringify(this.messages)
    );
  }

  /**
   * Load queue from sessionStorage
   */
  load(): void {
    const cachedLocal = this.deps.storage.getItem(STORAGE_KEYS.LOCAL_MESSAGES);
    const cachedLocalMessages =
      cachedLocal && (JSON.parse(cachedLocal) as GetMessagesResponseItem[]);

    if (cachedLocalMessages && cachedLocalMessages.length > 0) {
      this.messages = cachedLocalMessages;
    }
  }

  /**
   * Clear the entire queue
   */
  clear(): void {
    this.messages = [];
    this.save();
  }

  /**
   * Get processing lock status
   */
  isLocked(): boolean {
    return this.isProcessing;
  }

  /**
   * Acquire processing lock
   */
  acquireLock(): boolean {
    if (this.isProcessing) {
      return false;
    }
    this.isProcessing = true;
    return true;
  }

  /**
   * Release processing lock and resolve pending requests
   */
  releaseLock(): void {
    this.isProcessing = false;

    // Resolve all pending requests
    if (this.pendingProcessRequests.length > 0) {
      const pendingCallbacks = [...this.pendingProcessRequests];
      this.pendingProcessRequests = [];

      // Resolve all waiting promises immediately
      pendingCallbacks.forEach((callback) => callback());
    }
  }

  /**
   * Add a pending process request
   */
  addPendingRequest(callback: () => void): void {
    this.pendingProcessRequests.push(callback);
  }

  /**
   * Clear processing state (used during user logout)
   */
  clearProcessingState(): void {
    this.isProcessing = false;
    this.pendingProcessRequests.forEach((callback) => callback());
    this.pendingProcessRequests = [];
  }
}
